import {
  signInWithPopup,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { auth, googleProvider } from './config';

import axios from 'axios';
import config from '../config.json';

/**
 * Sign in with Google popup
 * @returns {Promise<{ user, idToken, googleProfile: { givenName?: string, familyName?: string } | null }>}
 */
export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const idToken = await result.user.getIdToken();
    const profile = result.additionalUserInfo?.profile;
    const googleProfile =
      profile && (profile.given_name != null || profile.family_name != null)
        ? {
            givenName:
              typeof profile.given_name === 'string' ? profile.given_name : '',
            familyName:
              typeof profile.family_name === 'string' ? profile.family_name : ''
          }
        : null;

    // Send token to backend
    const baseUrl = config.api.baseUrl || "http://localhost:8000";
    await axios.post(
      `${baseUrl}/api/auth/firebase-login`,
      {
        idToken,
        ...(googleProfile && {
          firstName: googleProfile.givenName,
          lastName: googleProfile.familyName
        })
      },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );

    return {
      user: result.user,
      idToken,
      googleProfile
    };
  } catch (error) {
    console.error('Google sign-in error:', error);
    throw error;
  }
};

/**
 * Sign out the current user
 * @returns {Promise<void>}
 */
export const signOutUser = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Sign-out error:', error);
    throw error;
  }
};

/**
 * Get current user's ID token
 * @param {boolean} forceRefresh - Force token refresh
 * @returns {Promise<string|null>}
 */
export const getCurrentIdToken = async (forceRefresh = false) => {
  const user = auth.currentUser;
  if (!user) return null;

  try {
    const token = await user.getIdToken(forceRefresh);
    return token;
  } catch (error) {
    console.error('Error getting ID token:', error);
    return null;
  }
};

/**
 * Set up auth state observer
 * @param {function} callback - Called with user object on auth state change
 * @returns {function} Unsubscribe function
 */
export const observeAuthState = (callback) => {
  return onAuthStateChanged(auth, callback);
};

/**
 * Check if user is currently authenticated
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  return !!auth.currentUser;
};
